import { lazy, Suspense, useMemo, useRef, useState } from 'react'
import { useNavigate, useParams } from '@tanstack/react-router'
import { useTranslation } from 'react-i18next'
import { newId } from '@gk/contracts'
import { areaLabel, useCourse } from '../data/geo.ts'
import { approxAreaM2, boundsOf, editableRing, formatArea, isSimpleRing, polygonFromRing, type Pos } from '../lib/geo.ts'
import { DrawTool } from '../map/DrawTool.tsx'
import { useBaseMap } from '../map/useBaseMap.ts'
import { BackLink, Banner, DisabledHint, Field, PendingBanner } from '../ui/components.tsx'
import { useWorkshopAction, useWriteAccess } from '../ui/hooks.ts'

const MapView = lazy(() => import('../map/MapView.tsx'))
const SOURCES = ['digitised_ortho', 'survey_gnss', 'device_gps', 'sketch'] as const
const DEFAULT_ACCURACY: Record<(typeof SOURCES)[number], string> = { digitised_ortho: '2', survey_gnss: '0.05', device_gps: '5', sketch: '' }

/** Draw (or redraw) one area on the aerial map. The outline is a REQUEST for a new version of the shape: the old one is kept, and the work done on
 *  it keeps pointing at it. */
export function AreaShapePage() {
  const { t, i18n } = useTranslation()
  const { areaId } = useParams({ strict: false }) as { areaId: string }
  const navigate = useNavigate()
  const edit = useWriteAccess('area.edit')
  const course = useCourse(i18n.language)
  const base = useBaseMap()
  const w = useWorkshopAction()
  const area = course.areas.find((a) => a.id === areaId)
  const current = course.areaVersions.find((v) => v.area_id === areaId && /,\s*\)$/.test(v.valid_during))
  const versionId = useRef(newId())
  const startRing = useMemo(() => editableRing(current?.geom), [current?.geom])
  const complex = !!current && !startRing
  const [pts, setPts] = useState<Pos[] | null>(null)
  const points = pts ?? startRing ?? []
  const [source, setSource] = useState<(typeof SOURCES)[number]>('digitised_ortho')
  const [accuracy, setAccuracy] = useState(DEFAULT_ACCURACY.digitised_ortho)
  const [note, setNote] = useState('')
  const fit = useMemo(() => boundsOf([current?.geom, ...course.areaCollection.features.map((f) => f.geometry)]), [current?.geom, course.areaCollection])

  if (!course.ready) return <p>{t('common.loading')}</p>
  if (!area) return <Banner tone="error">{t('course.areaMissing')}</Banner>
  const trail: string[] = []
  for (let a: typeof area | undefined = area; a && a.parent_id; a = course.areas.find((x) => x.id === a!.parent_id)) trail.unshift(areaLabel(a, i18n.language))
  const path = trail.slice(-3).join(' › ')
  const valid = points.length >= 3 && isSimpleRing(points)
  const problem = points.length === 0 ? null : points.length < 3 ? t('shape.tooFew') : !isSimpleRing(points) ? t('shape.crosses') : null
  const accNum = accuracy.trim() === '' ? null : Number(accuracy.replace(',', '.'))
  const accOk = source === 'sketch' || (accNum !== null && Number.isFinite(accNum) && accNum >= 0 && accNum <= 1000)

  const save = async () => {
    const ok = await w.run('area.revise@1', {
      area_id: area.id, version_id: versionId.current, geom: polygonFromRing(points), source,
      ...(source !== 'sketch' ? { accuracy_m: accNum! } : {}), ...(note.trim() ? { note: note.trim() } : {}),
    }, 'shape.saved')
    if (ok) { versionId.current = newId(); void navigate({ to: '/map', search: { area: area.id } }) }
  }

  return (
    <section className="flex flex-col gap-3" data-testid="shape-page">
      <BackLink to="/course" search={{ area: area.id }}>← {t('common.back')}</BackLink>
      <h1 className="text-2xl font-bold">{t('shape.title', { name: path })}</h1>
      {!edit.permitted && <Banner tone="warn">{t('shape.notAllowed')}</Banner>}
      {edit.readOnlyMode && <Banner tone="warn">{t('common.onlineOnly')}</Banner>}
      {!base.archive && <p className="rounded-lg border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-900">{t('shape.noBase')}</p>}
      {complex && <Banner tone="warn">{t('shape.complex')}</Banner>}
      <Suspense fallback={<div className="grid h-[55dvh] place-items-center rounded-xl border">{t('common.loading')}</div>}>
        <MapView className="h-[55dvh] min-h-80 overflow-hidden rounded-xl border-2" base={base.archive} areas={course.areaCollection} infra={course.infraCollection}
          selected={{ kind: 'area', id: area.id }} fit={fit} interactive={false}>
          {edit.allowed && <DrawTool mode="polygon" value={points} onChange={setPts} />}
        </MapView>
      </Suspense>
      <p className="meta">{t('shape.help')}</p>
      <p className="text-sm" data-testid="shape-live">{t('shape.points', { count: points.length })}{points.length >= 3 ? ` · ≈ ${formatArea(approxAreaM2(points), i18n.language)}` : ''}</p>
      {problem && <Banner tone="warn"><span data-testid="shape-problem">{problem}</span></Banner>}
      <div className="flex flex-wrap gap-2">
        <button className="btn !min-h-11" disabled={points.length === 0} onClick={() => setPts(points.slice(0, -1))} data-testid="shape-undo">{t('shape.undo')}</button>
        <button className="btn !min-h-11" disabled={points.length === 0} onClick={() => setPts([])} data-testid="shape-clear">{t('shape.clear')}</button>
        {startRing && <button className="btn !min-h-11" onClick={() => setPts(null)} data-testid="shape-reset">{t('shape.reset')}</button>}
      </div>
      {edit.allowed && (
        <div className="card flex flex-col gap-3">
          <div className="grid grid-cols-2 gap-3">
            <Field label={t('shape.source')} htmlFor="shape-source">
              <select id="shape-source" className="input" value={source} onChange={(e) => { const s = e.target.value as typeof source; setSource(s); setAccuracy(DEFAULT_ACCURACY[s]) }} data-testid="shape-source">
                {SOURCES.map((s) => <option key={s} value={s}>{t(`geoSource.${s}`)}</option>)}
              </select>
            </Field>
            {source !== 'sketch' && <Field label={t('shape.accuracy')} hint={t('shape.accuracyHelp')} htmlFor="shape-accuracy"><input id="shape-accuracy" className="input" inputMode="decimal" value={accuracy} onChange={(e) => setAccuracy(e.target.value)} data-testid="shape-accuracy" /></Field>}
          </div>
          <Field label={t('shape.note')} htmlFor="shape-note"><input id="shape-note" className="input" maxLength={500} value={note} onChange={(e) => setNote(e.target.value)} data-testid="shape-note" /></Field>
          {w.pending && <PendingBanner />}
          {w.error && <Banner tone="error"><span data-testid="shape-error">{w.error}</span></Banner>}
          {w.ok && <Banner tone="ok"><span data-testid="shape-ok">{w.ok}</span></Banner>}
          <DisabledHint show={!valid && !problem}>{t('shape.drawFirst')}</DisabledHint>
          <button className="btn btn-primary" disabled={!valid || !accOk || w.busy} onClick={() => void save()} data-testid="shape-save">{current ? t('shape.saveNew') : t('shape.saveFirst')}</button>
          {current && <p className="meta">{t('shape.keepsOld', { n: current.version_no })}</p>}
        </div>
      )}
    </section>
  )
}
